import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';

import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MovimentacaoService } from '../../../../../shared/services/apis/financas/movimentacao.service';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { PrintMovimentacaoRequest } from '../../../../../shared/services/requests/financas/print-movimentacao.request';
import { ListPessoaIdsRequest } from '../../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { MovimentacaoSaldoContaAtual } from '../../../../../shared/services/responses/financas/print-movimentacao.response';

interface RelatorioMovimentacaoItem {
    dataMovimentacao: string;
    descricaoContaFinanceira: string;
    descricao: string | null;
    cliente?: string;
    idPessoa?: number | null;
    descricaoCategoria: string | null;
    valor: number;
    isEntrada: boolean;
}

export interface ImpressaoExtratoMovimentacoesData {
    filtros: PrintMovimentacaoRequest;
    resumoFiltros?: string[];
}

@Component({
    selector: 'app-impressao-extrato-movimentacoes',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        LoadingBoxComponent
    ],
    templateUrl: './impressao-extrato-movimentacoes.component.html',
    styleUrls: ['./impressao-extrato-movimentacoes.component.css']
})
export class ImpressaoExtratoMovimentacoesComponent implements OnInit {

    public isLoading: boolean = false;
    public relatorioLancamentos: RelatorioMovimentacaoItem[] = [];
    public displayedColumns: string[] = ['dataMovimentacao', 'descricaoContaFinanceira', 'descricao', 'cliente', 'descricaoCategoria', 'valor'];
    public totalEntradas: number = 0;
    public totalSaidas: number = 0;
    public saldoInicial: number = 0;
    public quantidadeLancamentos: number = 0;
    public saldosContasAtuais: MovimentacaoSaldoContaAtual[] = [];
    public saldoAtualContasTotal: number = 0;

    public get saldoFinal(): number {
        return this.saldoInicial + this.totalEntradas - this.totalSaidas;
    }

    public get exibirObservacaoSaldo(): boolean {
        return Math.abs(this.saldoFinal - this.saldoAtualContasTotal) > 0.009;
    }

    constructor(
        public dialogRef: MatDialogRef<ImpressaoExtratoMovimentacoesComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImpressaoExtratoMovimentacoesData,
        private movimentacaoService: MovimentacaoService,
        private pessoaLookupService: PessoaLookupService,
        private alertService: AlertService
    ) { }

    ngOnInit(): void {
        this.buscarRelatorio();
    }

    private buscarRelatorio(): void {
        this.isLoading = true;
        this.movimentacaoService.getPrint(this.data.filtros).subscribe({
            next: (response) => {
                if (response.isError) {
                    this.isLoading = false;
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao gerar relatório.', AlertType.error);
                    this.fechar();
                    return;
                }

                this.relatorioLancamentos = (response.itens || []).map(item => ({
                    dataMovimentacao: item.dataMovimentacao,
                    descricaoContaFinanceira: item.descricaoContaFinanceira,
                    descricao: this.stripHtml(item.descricao || ''),
                    idPessoa: item.idPessoa,
                    cliente: undefined,
                    descricaoCategoria: item.descricaoCategoria,
                    valor: item.valor,
                    isEntrada: item.isEntrada
                }));
                this.totalEntradas = response.totalEntradas;
                this.totalSaidas = response.totalSaidas;
                this.saldoInicial = response.saldoInicial;
                this.quantidadeLancamentos = response.quantidadeLancamentos;
                this.saldosContasAtuais = response.saldosContasAtuais || [];
                this.saldoAtualContasTotal = response.saldoAtualContasTotal;

                this.carregarClientes();
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Erro ao gerar relatório.', AlertType.error);
                this.fechar();
            }
        });
    }

    private carregarClientes(): void {
        const idsPessoa = [...new Set(
            this.relatorioLancamentos
                .map(item => item.idPessoa)
                .filter((id): id is number => !!id)
        )];

        if (!idsPessoa.length) {
            this.isLoading = false;
            return;
        }

        const request = new ListPessoaIdsRequest();
        request.listIdPessoa = idsPessoa;

        this.pessoaLookupService.getListPessoaIds(request).subscribe({
            next: (response) => {
                if (!response.isError) {
                    const clientesPorId = new Map<number, string>();
                    (response.listaPessoa || []).forEach(pessoa => {
                        if (pessoa.idPessoa) {
                            clientesPorId.set(pessoa.idPessoa, pessoa.nome);
                        }
                    });

                    this.relatorioLancamentos = this.relatorioLancamentos.map(item => ({
                        ...item,
                        cliente: item.idPessoa ? clientesPorId.get(item.idPessoa) : undefined
                    }));
                }

                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    public imprimir(): void {
        const conteudoParaImprimir = document.querySelector('.modal-print-container');

        if (!conteudoParaImprimir) {
            return;
        }

        const janelaImpressao = window.open('', '', 'height=800,width=1100');
        if (!janelaImpressao) {
            return;
        }

        const styles = Array.from(document.styleSheets)
            .map(styleSheet => {
                try {
                    return Array.from(styleSheet.cssRules)
                        .map(rule => rule.cssText)
                        .join('');
                } catch {
                    return '';
                }
            })
            .join('');

        janelaImpressao.document.write(`
            <html>
                <head>
                    <title>Imprimir Extrato de Movimentações</title>
                    <base href="/">
                    <style>
                        ${styles}
                        html, body {
                            height: auto !important;
                            min-height: 0 !important;
                            overflow: visible !important;
                            margin: 20px;
                            font-family: Roboto, "Helvetica Neue", sans-serif;
                            color: #000;
                            background: #fff;
                        }
                        .no-print { display: none !important; }
                        .modal-print-container {
                            display: block !important;
                            width: 100% !important;
                            height: auto !important;
                            min-height: 0 !important;
                            overflow: visible !important;
                            box-shadow: none !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        .mat-mdc-table { background: white !important; }
                        .mat-mdc-header-cell { color: black !important; font-weight: bold !important; border-bottom: 2px solid black !important; }
                        .mat-mdc-cell { color: black !important; border-bottom: 1px solid #ddd !important; }
                    </style>
                </head>
                <body>
                    ${conteudoParaImprimir.innerHTML}
                </body>
            </html>
        `);

        janelaImpressao.document.close();
        janelaImpressao.focus();

        setTimeout(() => {
            janelaImpressao.print();
            janelaImpressao.close();
        }, 800);
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    public formatarValor(valor: number, isEntrada: boolean): string {
        const formatted = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);

        return `${isEntrada ? '+' : '-'} ${formatted}`;
    }

    private stripHtml(html: string): string {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.textContent || tmp.innerText || '').trim();
    }
}
